// app/javascript/components/quotes/builder/components/MetaCard.js
import React, { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import IconButton from "@react/shared/ui/IconButton"
import { useQuoteEditor } from "../context/QuoteEditorContext"
import clsx from "clsx"
import Card  from "@react/shared/ui/Card"
import Badge, { badgeVariants } from "@react/shared/ui/Badge"
import Label from "@react/shared/ui/Label"
import Input from "@react/shared/ui/Input"

const STATUS_TONE = {
  "In preparation":     "brand",
  "Demand":             "orange",
  "Sent":               "warning",
  "Rejected":           "neutral",
  "Accepted":           "success",
  "Invoiced":           "blue",
  "Partially invoiced": "blue",
}

// Dates come from the server formatted as "DD/MM/YYYY"; native <input type="date">
// expects ISO "YYYY-MM-DD". Helpers below are lenient — accept either format.
export const parseDate = v => {
  if (!v) return null
  let m = v.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (m) return new Date(+m[1], +m[2] - 1, +m[3])
  m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (m) return new Date(+m[3], +m[2] - 1, +m[1])
  return null
}
export const formatISO = d => {
  if (!d) return ""
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}
export const toISO = v => formatISO(parseDate(v))
export const daysBetween = (a, b) => {
  const da = parseDate(a), db = parseDate(b)
  if (!da || !db) return 0
  return Math.round((db - da) / 86400000)
}
export const addDays = (iso, days) => {
  const d = parseDate(iso)
  if (!d || !Number.isFinite(days)) return ""
  d.setDate(d.getDate() + days)
  return formatISO(d)
}

const ExternalLink = ({ icon, label, href }) => {
  if (!label) return null
  return (
    <a
      href      = {href}
      target    = "_blank"
      rel       = "noopener noreferrer"
      className = "inline-flex items-center gap-1.5 text-inherit no-underline cursor-pointer hover:underline hover:text-slate-900"
    >
      {icon && <FontAwesomeIcon icon={icon} className="text-slate-400 mr-1" />} {label}
    </a>
  )
}

// Click-to-edit title (matches V1's components/quotes/form/title.js).
export const TitleField = ({ title, onChange }) => {
  const [active, setActive] = useState(!title)
  if (!active && title) {
    return (
      <h1
        onClick   = {() => setActive(true)}
        className = "flex-1 min-w-0 m-0 px-2 py-1 rounded-md text-[1.4em] font-semibold text-slate-900 cursor-text hover:bg-slate-100"
      >
        {title}
      </h1>
    )
  }
  return (
    <input
      type        = "text"
      value       = {title || ""}
      autoFocus   = {!title}
      placeholder = {I18n.t("quotes.new.title_placeholder")}
      onChange    = {e => onChange(e.target.value)}
      onFocus     = {() => setActive(true)}
      onBlur      = {() => setActive(false)}
      className   = "flex-1 min-w-0 block px-2 py-1 rounded-md bg-white text-[1.4em] font-semibold text-slate-900 outline-none placeholder:text-slate-400 placeholder:font-normal"
    />
  )
}

export const DateField = ({ label, value, onChange, suffix }) => (
  <div className="flex items-center gap-2 min-w-0">
    <Label size="sm">{label}</Label>
    <Input
      type     = "date"
      size     = "sm"
      value    = {toISO(value)}
      onChange = {e => onChange(e.target.value)}
      className = "w-auto px-2 text-[13px]"
    />
    {suffix}
  </div>
)

// Click-to-edit chip showing validity days.
export const DaysChip = ({ days, onCommit }) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState("")
  const inputRef = React.useRef(null)

  React.useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editing])

  const start  = () => { setDraft(String(days ?? "")); setEditing(true) }
  const commit = () => {
    const n = parseInt(draft, 10)
    if (Number.isFinite(n) && n >= 0) onCommit(n)
    setEditing(false)
  }
  const cancel = () => { setDraft(""); setEditing(false) }

  if (editing) {
    return (
      <Badge tone="brand" variant="soft" size="xs" className="gap-1 tabular-nums">
        <input
          ref       = {inputRef}
          type      = "number"
          min       = "0"
          step      = "1"
          value     = {draft}
          onChange  = {e => setDraft(e.target.value)}
          onBlur    = {commit}
          onKeyDown = {e => {
            if (e.key === "Enter")  { e.preventDefault(); commit() }
            if (e.key === "Escape") { e.preventDefault(); cancel() }
          }}
          className = "w-10 bg-transparent border-0 outline-none text-[11px] font-semibold text-brand-600 text-right"
        />
        <span>j</span>
      </Badge>
    )
  }

  if (!days || days <= 0) return null

  return (
    <button
      type      = "button"
      onClick   = {start}
      title     = {I18n.t("quotes.builder.meta.validity_tooltip")}
      className = {clsx(
        badgeVariants({ tone: "brand", variant: "soft", size: "xs" }),
        "tabular-nums border border-transparent cursor-pointer hover:border-brand-500/40 hover:bg-brand-100 focus:outline-none focus:border-brand-500 focus:ring-[3px] focus:ring-brand-500/20",
      )}
    >
      {days} j
    </button>
  )
}

// The two folding commands sit at the same place in both states: what opens a
// frame must be found where it was closed.
const PaneToggles = ({ metaOpen, onToggleMeta, summaryOpen, onToggleSummary }) => (
  <div className="flex items-center gap-0.5 shrink-0">
    {onToggleMeta && (
      <IconButton
        size          = "sm"
        onClick       = {onToggleMeta}
        aria-expanded = {metaOpen}
        title         = {metaOpen
          ? I18n.t("quotes.builder.meta.collapse_header")
          : I18n.t("quotes.builder.meta.expand_header")}
      >
        <FontAwesomeIcon
          icon      = "chevron-up"
          className = {clsx("transition-transform duration-200", !metaOpen && "rotate-180")}
        />
      </IconButton>
    )}
    {onToggleSummary && (
      <IconButton
        size          = "sm"
        onClick       = {onToggleSummary}
        aria-expanded = {summaryOpen}
        title         = {summaryOpen
          ? I18n.t("quotes.builder.meta.hide_summary")
          : I18n.t("quotes.builder.meta.show_summary")}
      >
        <FontAwesomeIcon
          icon      = "angles-right"
          className = {clsx("transition-transform duration-200", !summaryOpen && "rotate-180")}
        />
      </IconButton>
    )}
  </div>
)

const PartnerChip = ({ partners, partnerId, onPick }) => {
  if (!partners || partners.length === 0) return null
  const partner = partners.find(item => String(item.id) === String(partnerId))
  if (partner) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <FontAwesomeIcon icon="landmark" className="text-slate-400 mr-1" /> {partner.name}
      </span>
    )
  }
  return (
    <button
      type      = "button"
      onClick   = {onPick}
      className = "inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border border-amber-300 bg-amber-50 text-amber-800 text-[12px] font-medium hover:bg-amber-100"
    >
      <FontAwesomeIcon icon="triangle-exclamation" /> {I18n.t("quotes.builder.meta.partner_missing")}
    </button>
  )
}

const MetaCard = ({ metaOpen = true, onToggleMeta, summaryOpen, onToggleSummary }) => {
  const { state, dispatch } = useQuoteEditor()
  const { number, status, title, client, site, creationDate, expirationDate, expirationDays, isTemplate, partners, partnerId } = state

  const statusLabel = status
    ? I18n.t(`quotes.bigcard.${status}`)
    : ""
  const statusTone = STATUS_TONE[status] || "neutral"

  const setTitle = value => dispatch({ type: "UPDATE_TITLE", payload: value })

  const setCreationDate = iso => {
    if (!iso) return
    const currentDays = daysBetween(creationDate, expirationDate)
    const days = currentDays > 0 ? currentDays : (expirationDays || 30)
    const newExpiration = addDays(iso, days)
    dispatch({
      type: "UPDATE_QUOTE_META",
      payload: { fields: { creationDate: iso, expirationDate: newExpiration || expirationDate, expirationDays: days } },
    })
  }

  const setExpirationDate = iso => {
    if (!iso) return
    const days = daysBetween(creationDate, iso)
    dispatch({
      type: "UPDATE_QUOTE_META",
      payload: { fields: { expirationDate: iso, expirationDays: days >= 0 ? days : expirationDays } },
    })
  }

  const daysLive = daysBetween(creationDate, expirationDate) || expirationDays

  const setValidityDays = days => {
    const newExpiration = addDays(creationDate, days)
    dispatch({
      type: "UPDATE_QUOTE_META",
      payload: { fields: { expirationDays: days, expirationDate: newExpiration || expirationDate } },
    })
  }

  const statusBadge = !isTemplate && status
    ? <Badge tone={statusTone} variant="soft" size="xs" className="whitespace-nowrap shrink-0">{statusLabel}</Badge>
    : isTemplate
      ? <Badge tone="info" variant="soft" size="xs" className="whitespace-nowrap shrink-0">{I18n.t("quotes.project_templates.badge")}</Badge>
      : null

  if (!metaOpen) {
    return (
      <Card padding="sm" className="mb-3 shrink-0 overflow-hidden">
        <div className="flex items-center gap-2.5 min-w-0">
          <button
            type      = "button"
            onClick   = {onToggleMeta}
            title     = {I18n.t("quotes.builder.meta.expand_header")}
            className = "flex-1 min-w-0 flex items-center gap-2.5 px-1 py-0.5 bg-transparent border-0 rounded-md text-left cursor-pointer hover:bg-slate-50"
          >
            {!isTemplate && number && (
              <span className="font-mono text-[11px] font-semibold text-slate-400 uppercase tracking-[0.06em] shrink-0">
                {number}
              </span>
            )}
            <span className="flex-1 min-w-0 truncate text-[15px] font-semibold text-slate-900">
              {title || I18n.t("quotes.new.title_placeholder")}
            </span>
          </button>
          {statusBadge}
          <PaneToggles
            metaOpen        = {metaOpen}
            onToggleMeta    = {onToggleMeta}
            summaryOpen     = {summaryOpen}
            onToggleSummary = {onToggleSummary}
          />
        </div>
      </Card>
    )
  }

  return (
    <Card padding="lg" className="mb-4 shrink-0 overflow-hidden">
      {!isTemplate && number && (
        <div className="font-mono text-[11.5px] font-semibold text-slate-400 uppercase tracking-[0.06em] mb-1">
          {number}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2.5 mb-2">
        <TitleField title={title} onChange={setTitle} />
        {statusBadge}
        <PaneToggles
          metaOpen        = {metaOpen}
          onToggleMeta    = {onToggleMeta}
          summaryOpen     = {summaryOpen}
          onToggleSummary = {onToggleSummary}
        />
      </div>

      {!isTemplate && (
        <div className="flex flex-wrap items-center gap-x-[18px] gap-y-2 mt-2.5 text-[0.9em] text-slate-500">
          {client?.id && <ExternalLink icon="user-circle" label={client.name} href={`/clients/${client.id}`} />}
          {site?.id   && <ExternalLink icon="building"    label={site.name}   href={`/sites/${site.id}`}     />}
          <PartnerChip partners={partners} partnerId={partnerId} onPick={() => dispatch({ type: "SET_TAB", payload: "settings" })} />
          <DateField label={I18n.t("quotes.builder.meta.issued_on")}   value={creationDate}   onChange={setCreationDate}   />
          <DateField
            label    = {I18n.t("quotes.builder.meta.expires_on")}
            value    = {expirationDate}
            onChange = {setExpirationDate}
            suffix   = {<DaysChip days={daysLive} onCommit={setValidityDays} />}
          />
        </div>
      )}

    </Card>
  )
}

export default MetaCard
